'use client'

/**
 * Thin storefront wrapper around FormRender that wires the analytics onEvent
 * callback and attribution context. Lives in the storefront (not shared-ui) to
 * keep the dependency direction clean: shared-ui has no analytics/consent imports;
 * the storefront glues them.
 *
 * Attribution fields sent on submit:
 *   sessionId      — existing session id (peekSessionId: null when no consent)
 *   landingPage    — location.pathname at render time (best-effort)
 *   referrer       — document.referrer
 *   consentState   — what the visitor's analytics choice was at render:
 *                    'granted' (consent mode, explicit allow), 'not_opted_out'
 *                    (opt-out mode, collected by default — NOT consent), or
 *                    'denied'. Asked of the privacy runtime, not inferred from
 *                    the session id: in opt-out mode every visitor has a
 *                    session, and a session minted before GPC was switched on
 *                    survives it.
 *   popupId        — not wired here (FormSection doesn't thread popup context
 *                    through yet; deferred until FormPopoverTrigger grows a
 *                    popupId prop)
 */

import { useMemo } from 'react'
import dynamic from 'next/dynamic'
import type { FormRenderProps } from '@sifly/shared-ui/content/FormRender'

import { trackMetaEvent } from '@/lib/metaPixel'
import { track, peekSessionId } from '@/lib/analytics'
import { privacyAllows, privacyMode } from '@/lib/privacyRuntime'

// Loaded lazily across a genuine client→client edge with no barrel in between,
// which is the only shape that actually splits (see storefront.md). FormRender
// drags zod + libphonenumber-js (~250 KB raw) and `ContentSections` — a Server
// Component on every content route — statically imports THIS module, so a
// static import here put both on every page whether or not it renders a form.
// No `loading` placeholder: a form_embed section is laid out by its own
// wrapper and is almost never the LCP element, so a spinner would be more
// visual noise than the brief gap it covers.
const FormRender = dynamic(
  () => import('@sifly/shared-ui/content/FormRender').then((m) => m.FormRender),
)

// Omit onEvent + attribution props that we own here. Prevents a caller's
// onEvent from being silently overridden (matches AnalyticsPopupHost).
export function AnalyticsFormRender(props: Omit<FormRenderProps, 'onEvent' | 'onSubmitted' | 'sessionId' | 'attributionContext'>) {
  // Compute sessionId + attribution together at mount time. useMemo deps [] is
  // correct: location, referrer, and session existence are stable for the
  // lifetime of a form mount. Co-memoizing guarantees sessionId and
  // consentState come from the SAME verdict, so they can never disagree.
  const { sessionId, attributionContext } = useMemo(() => {
    if (typeof window === 'undefined') return { sessionId: null, attributionContext: undefined }
    // The session joins the submission to the visit only while analytics is
    // allowed RIGHT NOW (an older session can outlive a GPC switch-on).
    const allowed = privacyAllows('analytics')
    const sid = allowed ? peekSessionId() : null
    return {
      sessionId: sid,
      attributionContext: {
        landingPage: window.location.pathname,
        referrer: document.referrer || undefined,
        consentState: !allowed ? 'denied' : privacyMode() === 'consent_required' ? 'granted' : 'not_opted_out',
        // popupId not wired: FormSection doesn't thread popup context through
        // the FormSection → AnalyticsFormRender path. Deferred until
        // FormPopoverTrigger grows a popupId prop and threads it here.
      },
    }
  }, [])

  return (
    <FormRender
      {...props}
      sessionId={sessionId}
      attributionContext={attributionContext}
      onSubmitted={(id) => trackMetaEvent('Lead', {}, `lead:${id}`)}
      onEvent={(type, meta) => {
        track(type, meta ?? {})
        // A lead form being filled in is the funnel's contact start. Without
        // this, contact_start fired only from contact-button clicks, so a
        // visitor who used the form never counted. Newsletter signup is its
        // own component and never passes through here.
        if (type === 'form_start') track('contact_start', { channel: 'form', form_id: meta?.form_id ?? '' })
      }}
    />
  )
}
